// Single source of truth untuk semua module metadata.
// Tambah module baru = tambah satu entry dalam MODULES object di bawah.
//
// Note: moduleId Firestore ≠ marketing/UI name. Lihat field `marketingName` untuk display copy.
// (cth `ai16saat-half` = "AI16SAAT" dgn BETA badge; `homeniche` = "Home Niche")

export const MODULES = {
  'ai8saat': {
    moduleId: 'ai8saat',
    marketingName: 'AI8SAAT',
    inPremiumPlan: true,
    releaseStatus: 'released',
    badge: null,
    // Fields below akan digunakan dalam Fasa 2 & 3 (per-task routing & cleanup)
    scriptKey: 'ai8saat',
    promptSchema: 'single-scene',
    uploadVia: 'webapp-or-extension',
  },
  'ai16saat-half': {
    moduleId: 'ai16saat-half',
    marketingName: 'AI16SAAT',
    inPremiumPlan: true,
    releaseStatus: 'released',
    badge: 'BETA',
    scriptKey: 'ai16saat',
    promptSchema: 'two-scene',
    uploadVia: 'extension-only',
  },
  'homeniche': {
    moduleId: 'homeniche',
    marketingName: 'HOME NICHE',
    inPremiumPlan: true,
    releaseStatus: 'released',
    badge: 'BETA',
    scriptKey: 'ai16saat',
    promptSchema: 'two-scene',
    uploadVia: 'extension-only',
  },
  'goyang': {
    moduleId: 'goyang',
    marketingName: 'GOYANG',
    inPremiumPlan: false, // belum release
    releaseStatus: 'coming-soon',
    badge: 'BETA',
    scriptKey: 'goyang',
    promptSchema: 'goyang-style',
    // Video GOYANG memang naik Cloud (kelas sama AI8SAAT — lihat gate local-only
    // extension: ai16saat*/homeniche/smartcustom/flownity sahaja yang local).
    // Tanda 'extension-only' dulu tinggalan zaman GOYANG belum release.
    uploadVia: 'webapp-or-extension',
    // Visible untuk non-admin tapi locked (tiada user yang ada 'goyang' dalam allowedModules).
    adminOnly: false,
  },
  'smartcustom': {
    moduleId: 'smartcustom',
    marketingName: 'SMART',
    inPremiumPlan: true, // released to all paid users (2026-04-30)
    releaseStatus: 'released',
    badge: 'NEW',
    // scriptKey null — Smart Custom rute script per-product ikut smartcustomSceneCount (1=ai8saat, 2=ai16saat)
    scriptKey: null,
    promptSchema: 'manual', // user input prompts manually, no AI generation
    uploadVia: 'extension-only',
    // Tile visual (since not in Firestore modules/ collection — registry adalah source)
    icon: 'auto_fix_high',
    color: '#06b6d4',
    order: 5,
    // Admin-only access (nampak tapi locked utk semua non-admin). isPremiumAddon = wildcard '*' TIDAK auto-grant.
    adminOnly: false,
    isPremiumAddon: true,
  },
};

// ─── Registry-only modules (injected to App.jsx modules array) ───
// Modul yang TIADA Firestore doc — biasanya manual mode tanpa masterPrompt.
export function listRegistryOnlyModules() {
  return Object.values(MODULES).filter(m => m.promptSchema === 'manual');
}

// ─── Per-product effective script resolution ───
// Smart Custom rute script ikut smartcustomSceneCount (1=ai8saat, 2=ai16saat).
// Untuk module lain, terus baca scriptKey dari registry.
export function getEffectiveScriptKey(product) {
  const moduleId = product?.moduleId || 'ai8saat';

  /* ★ ENJIN DINAMIK (19 Ogos) — 1-5 babak ikut tetapan PELANGGAN.
     Penanda `flowEngine` dicop pada kad SEMASA larian Agent Saya berjaya
     (lihat WorkspaceView, laluan resipi agent). Kelak "Formula Sendiri"
     mengecop penanda yang SAMA.

     ⛔ SENGAJA penanda eksplisit, bukan "semua kad smartcustom". Pelanggan
        hari ini ada kad Smart Custom yang dibuat sendiri (1-2 babak) dan
        berjalan atas enjin rasmi. Menghalakan mereka semua ke enjin baharu
        akan menukar perangai produk yang mereka guna — dan enjin baharu
        hanya wujud selepas mereka pasang semula extension. */
  if (product?.flowEngine === 'dinamik') return 'dinamik';

  if (moduleId === 'smartcustom') {
    const scene = product?.smartcustomSceneCount || 1;
    return scene === 2 ? 'ai16saat' : 'ai8saat';
  }
  return getModule(moduleId).scriptKey || 'ai8saat';
}

// ─── Bilangan babak (scene count) ───
// Terima sama ada produk (ada moduleId) atau objek module (ada id).
// smartcustom: ikut smartcustomSceneCount per-produk.
// module lain: ikut field sceneCount kalau ada (N-scene module), atau derive dari promptSchema.
export function getSceneCount(moduleOrProduct) {
  const moduleId = moduleOrProduct?.moduleId || moduleOrProduct?.id || 'ai8saat';

  /* ★ Kad enjin dinamik membawa bilangan babak PELANGGAN pada `sceneCount`.
     Inilah pepijat yang menyebabkan "pilih 3 babak, video tak jadi": bilangan
     babak ditulis pada kad, tetapi tiada siapa membacanya — pemilih hanya
     melihat `smartcustomSceneCount` yang laluan agent tidak pernah tulis.
     Dikepit 1-5 supaya nilai rosak tidak boleh meminta 40 babak. */
  if (moduleOrProduct?.flowEngine === 'dinamik') {
    return Math.max(1, Math.min(5, Number(moduleOrProduct?.sceneCount) || 1));
  }

  if (moduleId === 'smartcustom') {
    return moduleOrProduct?.smartcustomSceneCount || 1;
  }
  const m = getModule(moduleId);
  if (m.sceneCount) return m.sceneCount;            // N-scene module (cth 5)
  if (m.promptSchema === 'two-scene') return 2;     // AI16SAAT / Home Niche
  return 1;                                          // single-scene / manual / goyang
}

// Display-friendly module label, dengan scene suffix untuk smartcustom
// Contoh: "Smart Custom (1 scene)" atau "AI16SAAT" (no suffix)
export function getDisplayLabelForProduct(product) {
  if (product?.kosong) return 'Blank card';   // kad kosong (user 2 Nov) — tiada module sebenar
  const moduleId = product?.moduleId || 'ai8saat';
  const m = getModule(moduleId);
  if (product?.flowEngine === 'dinamik') {
    const babak = Math.max(1, Math.min(5, Number(product?.sceneCount) || 1));
    return `${m.marketingName} (${babak} babak)`;
  }
  if (moduleId === 'smartcustom') {
    const scene = product?.smartcustomSceneCount || 1;
    return `${m.marketingName} (${scene} scene)`;
  }
  return m.marketingName;
}

// Boolean — adakah upload video via webapp disekat untuk module ni?
// Smart Custom + AI16SAAT block webapp upload (extension Mass Video sahaja).
export function isUploadBlockedForModule(moduleId) {
  const m = getModule(moduleId || 'ai8saat');
  return m.uploadVia === 'extension-only';
}

// ─── Helper Functions ───

// Runtime modules — module ciptaan admin via UI (Firestore modules/ doc) yang
// TIADA dalam registry built-in di atas. Didaftar masa App load (registerRuntimeModules).
// getModule() rujuk built-in dulu (menang), lepas tu runtime, lepas tu fallback ai8saat.
let RUNTIME_MODULES = {};

// Daftar module Firestore yang bukan built-in supaya getModule() (+ semua helper
// yang bergantung padanya: getEffectiveScriptKey, getDisplayLabelForProduct,
// isUploadBlockedForModule, tile badge/gating) pulangkan engine/gating yang betul.
export function registerRuntimeModules(list) {
  const map = {};
  for (const m of (Array.isArray(list) ? list : [])) {
    if (!m || !m.id) continue;
    if (MODULES[m.id]) continue; // built-in menang — jangan override
    map[m.id] = {
      moduleId: m.id,
      marketingName: m.marketingName || m.name || m.id,
      inPremiumPlan: m.inPremiumPlan ?? false,
      releaseStatus: m.releaseStatus || 'released',
      badge: m.badge || null,
      scriptKey: m.scriptKey ?? null,
      promptSchema: m.promptSchema || 'single-scene',
      uploadVia: m.uploadVia || 'webapp-or-extension',
      adminOnly: m.adminOnly ?? false,
      isPremiumAddon: m.isPremiumAddon ?? false,
      sceneCount: m.sceneCount ?? null, // N-scene module — bilangan babak (null = derive dari promptSchema)
      // ★ Enjin RESIPI (17 Okt — PLAN_FLOW_RESIPI.md): borang tempahan module, cth { gambar: 'sama' }. Dihantar
      //   ke robot sebagai product.resipi (WorkspaceView productDataForExtension). Bukan objek = null.
      resipi: (m.resipi && typeof m.resipi === 'object' && !Array.isArray(m.resipi)) ? { gambar: String(m.resipi.gambar || 'sama') } : null,
      icon: m.icon || 'extension',
      color: m.color || '#94a3b8',
      order: m.order ?? 99,
    };
  }
  RUNTIME_MODULES = map;
}

export function getModule(moduleId) {
  return MODULES[moduleId] || RUNTIME_MODULES[moduleId] || MODULES['ai8saat'];
}

export function listAllModules() {
  return Object.values(MODULES);
}

// Modules yang dimasukkan dalam Premium plan — guna untuk marketing copy
// (PricingView, StartGate, AdminView). Order ikut insertion order dalam MODULES.
export function listPremiumPlanModules() {
  return Object.values(MODULES).filter(m => m.inPremiumPlan);
}

export function getMarketingName(moduleId) {
  return getModule(moduleId).marketingName;
}
